import { useState, useEffect } from 'react';

interface A11ySettings {
  textScale: number;
  highContrast: boolean;
  readingMode: boolean;
}

const DEFAULTS: A11ySettings = { textScale: 100, highContrast: false, readingMode: false };
const SCALES = [90, 100, 115, 130];

function loadSettings(): A11ySettings {
  try {
    const raw = localStorage.getItem('nwgsv_a11y');
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

export default function AccessibilityToolbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<A11ySettings>(loadSettings);

  useEffect(() => {
    (document.body.style as any).zoom = settings.textScale === 100 ? '' : `${settings.textScale}%`;
    document.body.classList.toggle('a11y-high-contrast', settings.highContrast);
    document.body.classList.toggle('a11y-reading-mode', settings.readingMode);
    localStorage.setItem('nwgsv_a11y', JSON.stringify(settings));
  }, [settings]);

  function stepScale(direction: 1 | -1) {
    setSettings(prev => {
      const idx = SCALES.indexOf(prev.textScale);
      const nextIdx = Math.min(Math.max(idx + direction, 0), SCALES.length - 1);
      return { ...prev, textScale: SCALES[nextIdx] };
    });
  }

  return (
    <div className="a11y-root">
      <button
        className="a11y-btn"
        onClick={() => setIsOpen(o => !o)}
        aria-label="Accessibility options"
        title="Accessibility options"
        data-testid="a11y-toolbar-btn"
      >
        Aa
      </button>

      {isOpen && (
        <div className="a11y-panel" data-testid="a11y-panel">
          <div className="a11y-panel-title">ACCESSIBILITY</div>

          <div className="a11y-row">
            <span>Text size</span>
            <div className="a11y-scale-controls">
              <button
                onClick={() => stepScale(-1)}
                disabled={settings.textScale === SCALES[0]}
                data-testid="a11y-text-decrease"
                aria-label="Decrease text size"
              >
                A−
              </button>
              <span className="a11y-scale-value" data-testid="a11y-text-scale">{settings.textScale}%</span>
              <button
                onClick={() => stepScale(1)}
                disabled={settings.textScale === SCALES[SCALES.length - 1]}
                data-testid="a11y-text-increase"
                aria-label="Increase text size"
              >
                A+
              </button>
            </div>
          </div>

          <div className="a11y-row">
            <span>High contrast</span>
            <button
              className={`a11y-toggle ${settings.highContrast ? 'on' : ''}`}
              onClick={() => setSettings(p => ({ ...p, highContrast: !p.highContrast }))}
              data-testid="a11y-contrast-toggle"
              aria-pressed={settings.highContrast}
            >
              {settings.highContrast ? 'ON' : 'OFF'}
            </button>
          </div>

          <div className="a11y-row">
            <span>Reading mode</span>
            <button
              className={`a11y-toggle ${settings.readingMode ? 'on' : ''}`}
              onClick={() => setSettings(p => ({ ...p, readingMode: !p.readingMode }))}
              data-testid="a11y-reading-toggle"
              aria-pressed={settings.readingMode}
            >
              {settings.readingMode ? 'ON' : 'OFF'}
            </button>
          </div>

          <button
            className="a11y-reset"
            onClick={() => setSettings(DEFAULTS)}
            data-testid="a11y-reset"
          >
            Reset to defaults
          </button>
        </div>
      )}
    </div>
  );
}
